import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FilterSection } from '../../../components/common/filter/FilterSection/FilterSection';
import { NoFilteredItem } from '../../../components/common/filter/NoFilteredItem';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { IProductFilter } from '../../../constants/filter/productFilter.constant';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetNewProductQuery } from '../../../hooks/api/product/queries/useGetNewProduct.query';
import { useGetProductsQuery } from '../../../hooks/api/product/queries/useGetProducts.query';
import { useAddFilterOptions } from '../../../hooks/filter/useAddFilterOptions';
import { useCheckSearchParamUpdate } from '../../../hooks/filter/useCheckSearchParamUpdate';
import { useUpdatePLPSearchParam } from '../../../hooks/filter/useUpdatePLPSearchParam';
import { useUpdateProductFilterStates } from '../../../hooks/filter/useUpdateProductFilterStates';
import { useMarkNotificationAsRead } from '../../../hooks/page/useMarkNotificationAsRead';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppSelector } from '../../../hooks/store/useStore';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';
import { createProductFilterSearchParams } from '../../../utils/filter/createProductFilterSearchParams.util';
import {
  IProductForDetailCard,
  ProductDetailCard,
} from '../../../views/Product/listView/ProductDetailCard/ProductDetailCard';
import { ProductVerticalListDetailCardGroup } from '../../../views/Product/listView/ProductDetailCard/ProductVerticalListDetailCardGroup';
import { SellTractorButton } from '../../OpenMarket/components/SellTractorButton';
import { MarketAlarmButton } from './components/MarketAlarmButton';
import { ProductCategoryDescription } from './components/ProductCategoryDescription';
import { ProductListPageWrapper } from './components/ProductListPageWrapper';

export const ProductListPage = () => {
  useAddFilterOptions({ data: 'brands', filterType: 'productFilter', applicableMachineType: 'tractor' });
  useCheckSearchParamUpdate({ filterType: 'productFilter' });
  useUpdateProductFilterStates();
  useUpdatePLPSearchParam();
  const { userProfile } = useGetUserProfile();

  const productFilterStates = useAppSelector((state) => state.filter.productFilter);

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: false }} headerTitle={'인증트랙터'} hasMyInfoButton />
        <FilterSection filterType={'productFilter'} />
        <MarketAlarmButton pageType={'PLP'} />
      </>
    ),
    fixedHeaderHeight: userProfile?.advertisementTermsAgreement ? 110 : 146,
    fixedFooter: <SellTractorButton pageType={'plp'} />,
    fixedFooterHeight: 0,
  });

  const { data: products, isLoading: isGetProductsQueryLoading } = useGetProductsQuery({
    searchParam: createProductFilterSearchParams({ filters: productFilterStates.globalFilters as IProductFilter[] }),
  });

  const { data: newProduct } = useGetNewProductQuery();
  useMarkNotificationAsRead(newProduct, 'newMarketProductInfo');

  useEffect(() => {
    // 매물이 바뀔 때 마다(= 검색 결과가 바뀔 때 마다) 경우 PLP의 Scroll Y 값을 0 으로 변경
    if (products) {
      window.scrollTo(0, 0);
    }
  }, [products]);

  return (
    <>
      <Helmet>
        <title>{pageInfo.productListPage.title}</title>
      </Helmet>

      <ProductListPageWrapper>
        {productFilterStates.globalFilters
          .find((filter) => filter.key === 'filter_category')
          ?.options.find((option) => option.isSelected && (option.value === 'all' || option.value === 'PLUVA')) && (
          <ProductCategoryDescription />
        )}

        {isGetProductsQueryLoading && <AnimateLoading />}
        {!isGetProductsQueryLoading && products && products.length === 0 && (
          <NoFilteredItem filterType={'productFilter'} />
        )}
        {!isGetProductsQueryLoading && products && products.length > 0 && (
          <ProductVerticalListDetailCardGroup>
            {products.map((product) => (
              <ProductDetailCard key={product.id} product={product as IProductForDetailCard} />
            ))}
          </ProductVerticalListDetailCardGroup>
        )}
      </ProductListPageWrapper>
    </>
  );
};
